import { useEffect, useMemo, useRef, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { BarChart3, TrendingUp, CalendarCheck, ArrowUpRight, ArrowDownRight, Minus, Download, Files, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
  LineChart, Line,
} from "recharts";

function AttendanceTrendPanel({ reports }) {
  const byChild = {};
  reports.forEach((r) => {
    if (!byChild[r.child_id]) byChild[r.child_id] = { name: r.child_name, points: [] };
    byChild[r.child_id].points.push({ month: r.month, rate: r.attendance.rate });
  });
  const kids = Object.entries(byChild).map(([id, v]) => ({
    id, name: v.name, points: v.points.sort((a, b) => a.month.localeCompare(b.month)).slice(-6),
  }));
  if (kids.length === 0) return null;
  const monthsSet = new Set();
  kids.forEach((k) => k.points.forEach((p) => monthsSet.add(p.month)));
  const months = Array.from(monthsSet).sort().slice(-6);
  const data = months.map((m) => {
    const row = { month: m };
    kids.forEach((k) => {
      const p = k.points.find((x) => x.month === m);
      row[k.name] = p ? p.rate : null;
    });
    return row;
  });
  const palette = ["#0284C7", "#10B981", "#F59E0B", "#F43F5E", "#6366F1", "#0EA5E9", "#22C55E", "#EC4899"];
  return (
    <Card data-testid="attendance-trend-panel" className="border-slate-200/70 card-elevated">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <TrendingUp className="w-5 h-5" />
          </div>
          <div>
            <div className="font-black text-slate-900">اتجاه الحضور — آخر ٦ أشهر</div>
            <div className="text-xs text-slate-500">نسبة الحضور لكل طفل عبر الزمن</div>
          </div>
        </div>
        <div className="h-64" dir="ltr">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fill: "#475569", fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fill: "#475569", fontSize: 11 }} />
              <Tooltip contentStyle={{ borderRadius: 12, borderColor: "#E2E8F0" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {kids.map((k, i) => (
                <Line key={k.id} type="monotone" dataKey={k.name} stroke={palette[i % palette.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function DeltaBadge({ delta }) {
  if (delta === 0) return (
    <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
      <Minus className="w-3 h-3" /> ثابت
    </span>
  );
  const up = delta > 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${up ? "text-emerald-700 bg-emerald-100" : "text-rose-700 bg-rose-100"}`}>
      {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      {up ? "+" : ""}{delta}%
    </span>
  );
}

function ReportCard({ r, chartData, attDelta, iepDelta }) {
  const ref = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const exportPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/monthly-reports/${r.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${r.child_name}-${r.month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error("تعذر تصدير الـ PDF"); }
    finally { setDownloading(false); }
  };

  return (
    <Card data-testid={`monthly-${r.id}`} className="card-elevated border-slate-200/70">
      <CardContent className="p-6">
        <div ref={ref} className="bg-white p-2">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-sky-700">
                {r.month}{r.prev ? ` مقابل ${r.prev.month}` : ""}
              </div>
              <div className="text-2xl font-black text-slate-900 mt-1">{r.child_name}</div>
              <div className="text-[11px] text-slate-500 mt-1">مركز هابي داي — تقرير شهري</div>
            </div>
            <div className="w-11 h-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center">
              <BarChart3 className="w-5 h-5" />
            </div>
          </div>

          {r.prev && (
            <div className="h-52 -mx-2 mb-4" dir="ltr">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" tick={{ fill: "#475569", fontSize: 12 }} />
                  <YAxis domain={[0, 100]} tick={{ fill: "#475569", fontSize: 12 }} />
                  <Tooltip cursor={{ fill: "rgba(2,132,199,0.05)" }} contentStyle={{ borderRadius: 12, borderColor: "#E2E8F0" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey={r.prev.month} fill="#94A3B8" radius={[8, 8, 0, 0]} maxBarSize={48} />
                  <Bar dataKey={r.month} fill="#0284C7" radius={[8, 8, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                  <CalendarCheck className="w-4 h-4 text-emerald-600" /> نسبة الحضور
                </div>
                <div className="flex items-center gap-2">
                  {attDelta !== null && <DeltaBadge delta={attDelta} />}
                  <span className="text-lg font-black text-emerald-700">{r.attendance.rate}%</span>
                </div>
              </div>
              <Progress value={r.attendance.rate} className="h-2.5" />
              <div className="text-xs text-slate-500 mt-2">
                {r.attendance.present} حاضر · {r.attendance.absent} غائب · {r.attendance.excused} بعذر (من أصل {r.attendance.total})
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                  <TrendingUp className="w-4 h-4 text-sky-600" /> متوسط تقدم IEP
                </div>
                <div className="flex items-center gap-2">
                  {iepDelta !== null && <DeltaBadge delta={iepDelta} />}
                  <span className="text-lg font-black text-sky-700">{r.iep.avg_progress}%</span>
                </div>
              </div>
              <Progress value={r.iep.avg_progress} className="h-2.5" />
              <div className="text-xs text-slate-500 mt-2">{r.iep.count} هدف تعليمي</div>
            </div>

            {r.iep.goals && r.iep.goals.length > 0 && (
              <div className="pt-3 border-t border-slate-100 space-y-2">
                <div className="text-xs font-bold uppercase text-slate-500">تفاصيل الأهداف</div>
                {r.iep.goals.map((g, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="flex-1 truncate text-slate-700">{g.title}</span>
                    <span className="font-bold text-emerald-700">{g.progress}%</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <Button
          data-testid={`download-pdf-${r.id}`}
          onClick={exportPdf}
          disabled={downloading}
          variant="outline"
          className="mt-4 w-full h-11 gap-2 text-sky-700 border-sky-200 hover:bg-sky-50"
        >
          <Download className="w-4 h-4" />
          {downloading ? "جاري التحضير..." : "تحميل PDF"}
        </Button>
      </CardContent>
    </Card>
  );
}

export default function MonthlyReportsPage() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    const { data } = await api.get("/monthly-reports");
    setReports(data);
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/monthly-reports/generate");
      toast.success(`تم توليد تقارير شهر ${data.month} — الإشعارات أُرسلت`);
      await load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setGenerating(false); }
  };

  // Group by child, sort months desc, then pair each report with its previous month
  const grouped = useMemo(() => {
    const byChild = {};
    reports.forEach((r) => {
      if (!byChild[r.child_id]) byChild[r.child_id] = [];
      byChild[r.child_id].push(r);
    });
    Object.values(byChild).forEach((arr) => arr.sort((a, b) => b.month.localeCompare(a.month)));
    // flatten with prev attached
    const flat = [];
    reports.forEach((r) => {
      const list = byChild[r.child_id];
      const idx = list.findIndex((x) => x.id === r.id);
      const prev = list[idx + 1] || null;
      flat.push({ ...r, prev });
    });
    return flat;
  }, [reports]);

  return (
    <div className="space-y-6" data-testid="monthly-reports-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900">التقارير الشهرية</h1>
          <p className="text-slate-500 mt-2">
            ملخص الحضور وتقدم أهداف IEP مع مقارنة بالشهر السابق — يُرسل تلقائياً لكل ولي أمر أول كل شهر
          </p>
        </div>
        {user.role === "admin" && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              data-testid="download-year-xlsx-btn"
              onClick={async () => {
                const now = new Date();
                // Academic year starts in September; if before Sep, use last Sep
                const startYear = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
                const startMonth = `${startYear}-09`;
                try {
                  const res = await api.get(`/attendance/export-range.xlsx?start_month=${startMonth}&months=12`, { responseType: "blob" });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `الحضور-عام-${startYear}-${startYear + 1}.xlsx`;
                  document.body.appendChild(a); a.click(); a.remove();
                  setTimeout(() => URL.revokeObjectURL(url), 5000);
                  toast.success("تم تصدير تقرير العام الدراسي");
                } catch (e) { toast.error(extractError(e)); }
              }}
              variant="outline"
              className="h-12 gap-2 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
            >
              <FileSpreadsheet className="w-4 h-4" /> XLSX عام دراسي كامل
            </Button>
            <Button
              data-testid="download-batch-pdf-btn"
              onClick={async () => {
                if (reports.length === 0) return;
                const month = reports[0].month; // most recent
                try {
                  const res = await api.get(`/monthly-reports-batch/pdf?month=${encodeURIComponent(month)}`, { responseType: "blob" });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `تقارير-${month}.pdf`;
                  document.body.appendChild(a); a.click(); a.remove();
                  setTimeout(() => URL.revokeObjectURL(url), 5000);
                } catch (e) { toast.error(extractError(e)); }
              }}
              disabled={reports.length === 0}
              variant="outline"
              className="h-12 gap-2 text-sky-700 border-sky-200 hover:bg-sky-50"
            >
              <Files className="w-4 h-4" /> PDF مُجمّع لكل الأطفال
            </Button>
            <Button
              data-testid="generate-reports-btn"
              onClick={generate}
              disabled={generating}
              className="h-12 gap-2 bg-sky-600 hover:bg-sky-700"
            >
              <BarChart3 className="w-4 h-4" />
              {generating ? "جاري التوليد..." : "توليد تقرير الشهر السابق"}
            </Button>
          </div>
        )}
      </div>

      {grouped.length > 0 && <AttendanceTrendPanel reports={reports} />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {grouped.map((r) => {
          const attDelta = r.prev ? r.attendance.rate - r.prev.attendance.rate : null;
          const iepDelta = r.prev ? r.iep.avg_progress - r.prev.iep.avg_progress : null;
          const chartData = [
            {
              name: "الحضور %",
              [r.prev ? r.prev.month : "سابق"]: r.prev ? r.prev.attendance.rate : 0,
              [r.month]: r.attendance.rate,
            },
            {
              name: "تقدم IEP %",
              [r.prev ? r.prev.month : "سابق"]: r.prev ? r.prev.iep.avg_progress : 0,
              [r.month]: r.iep.avg_progress,
            },
          ];
          return (
            <ReportCard key={r.id} r={r} chartData={chartData} attDelta={attDelta} iepDelta={iepDelta} />
          );
        })}
        {grouped.length === 0 && (
          <Card className="md:col-span-2">
            <CardContent className="p-16 text-center text-slate-400">
              لا توجد تقارير بعد
              {user.role === "admin" && <div className="mt-3 text-xs">اضغط "توليد تقرير الشهر السابق" للبدء</div>}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
